import { useCallback, useEffect, useState } from "react";
import { readSavedTheme, saveTheme, type Theme } from "../model/theme.ts";
import { readSavedSidebarVisible, saveSidebarVisible } from "../model/sidebar.ts";
import { readSavedLanguage, saveLanguage, type Language } from "../i18n/language.ts";
import {
  readSavedElementDefaults,
  saveElementDefaults,
  type ElementDefaults,
} from "../model/elementDefaults.ts";

/**
 * Per-browser UI preferences, independent of any project: theme, language,
 * sidebar visibility, and the defaults applied to every brand-new element
 * drawn on the map. Each reads its saved value once at mount and persists
 * on every change — none of this is undo/redo-able or reset on project
 * switch.
 */
export function useAppPreferences() {
  const [theme, setTheme] = useState<Theme>(() => readSavedTheme());
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    saveTheme(theme);
  }, [theme]);
  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === "light" ? "dark" : "light"));
  }, []);

  const [language, setLanguage] = useState<Language>(() => readSavedLanguage());
  useEffect(() => {
    saveLanguage(language);
  }, [language]);

  const [sidebarVisible, setSidebarVisible] = useState(() => readSavedSidebarVisible());
  useEffect(() => {
    saveSidebarVisible(sidebarVisible);
  }, [sidebarVisible]);

  // Applied to every brand-new element drawn on the map — per-browser, like
  // theme/language above, not per-project.
  const [elementDefaults, setElementDefaults] = useState<ElementDefaults>(() =>
    readSavedElementDefaults()
  );
  useEffect(() => {
    saveElementDefaults(elementDefaults);
  }, [elementDefaults]);

  return {
    theme,
    setTheme,
    toggleTheme,
    language,
    setLanguage,
    sidebarVisible,
    setSidebarVisible,
    elementDefaults,
    setElementDefaults,
  };
}
